'use client';

import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

import { EXAMPLE_DRUG_IDS } from '../lib/example-drugs';
import type { ChplQuote, ViewChplOptions } from '../types';
import type { InteractionCheckResult } from '../types';
import { ChplViewer, type DocumentType, type ViewMode } from './chpl-viewer';
import { DrugInteractionsContainer } from './drug-interactions-container';
import type { Drug } from './drug-search';
import type { PaywallVariant } from './interaction-paywall';

// Hook for resizable panel
function useResizablePanel(initialWidth = 55, minWidth = 25, maxWidth = 70) {
  const [panelWidth, setPanelWidth] = useState(initialWidth);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(initialWidth);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDragging.current = true;
      startX.current = e.clientX;
      startWidth.current = panelWidth;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [panelWidth],
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;

      const deltaX = startX.current - e.clientX;
      const deltaPercent = (deltaX / window.innerWidth) * 100;
      const newWidth = Math.min(
        maxWidth,
        Math.max(minWidth, startWidth.current + deltaPercent),
      );
      setPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [minWidth, maxWidth]);

  return { panelWidth, handleMouseDown, isDragging };
}

// Custom hook for auto-hide header on scroll
export function useScrollDirection(threshold = 10) {
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    const updateScrollDirection = () => {
      const scrollY = window.scrollY;
      const direction = scrollY > lastScrollY.current ? 'down' : 'up';

      // Hide after scrolling down past threshold, show immediately on scroll up
      if (direction === 'down' && scrollY > threshold) {
        setIsVisible(false);
      } else if (direction === 'up') {
        setIsVisible(true);
      }

      // Always show at top of page
      if (scrollY < 5) {
        setIsVisible(true);
      }

      lastScrollY.current = scrollY > 0 ? scrollY : 0;
      ticking.current = false;
    };

    const onScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(updateScrollDirection);
        ticking.current = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);

  return isVisible;
}

interface ChplViewerState {
  quote: ChplQuote;
  /** Unique key to force remount when clicking Rozdział 4.5 on same quote */
  viewKey: number;
  /** Initial view mode (markdown or pdf) */
  initialViewMode: ViewMode;
  /** When true, scroll/navigate to chapter 4.5; when false, start at beginning */
  scrollToChapter45: boolean;
  /** Document type: chpl or ulotka */
  documentType: DocumentType;
  /** Whether this drug has a ChPL PDF available */
  hasChplPdf: boolean;
  /** Whether this drug has an ulotka available */
  hasUlotka: boolean;
  /** Pre-known extraction availability from drug metadata */
  hasChplExtraction?: boolean;
}

export interface InitialPanel {
  type: 'chpl' | 'ulotka';
  drug: Drug;
}

export interface DrugInteractionsPageShellProps {
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether the auth state is still loading (prevents premature paywall variant) */
  isAuthLoading?: boolean;
  /** Whether the user has an active trial (only used when authenticated) */
  hasActiveTrial?: boolean;
  /** Whether the authenticated user needs to activate their free trial first */
  needsTrialActivation?: boolean;
  /** Callback to trigger the trial activation wizard */
  onActivateTrial?: () => void;
  /** Initial drugs to preload in the search (e.g., from URL params for SEO pages) */
  initialDrugs?: Drug[];
  /** Header content (auth buttons, navigation, etc.). Can be a render function receiving `{ handleLoadExample }`. */
  header?:
    | ReactNode
    | ((ctx: { handleLoadExample: () => Promise<void> }) => ReactNode);
  /** Announcement bar rendered above the header; scrolls away with the page */
  topBar?: ReactNode;
  /** Height of `topBar` in px — the fixed header is offset by it while the bar is on screen */
  topBarHeight?: number;
  /** Logo/branding content shown above search. Can be a render function receiving `{ hasDrugs }` context. */
  branding: ReactNode | ((ctx: { hasDrugs: boolean }) => ReactNode);
  /** Footer content */
  footer: (props: { isViewerOpen: boolean; panelWidth: number }) => ReactNode;
  /** Callback to expose reset function */
  onResetRef?: (reset: () => void) => void;
  /** Callback to expose setDrugs function */
  onSetDrugsRef?: (setDrugs: (drugs: Drug[]) => void) => void;
  /** Content rendered between main content and footer (e.g. features carousel).
   *  Can be a render function receiving `{ hasDrugs }` context. */
  beforeFooter?: ReactNode | ((ctx: { hasDrugs: boolean }) => ReactNode);
  /** Enable URL synchronization for sharing (syncs drugs and check state to URL) */
  enableUrlSync?: boolean;
  /** Auto-open a document panel on mount (from URL state) */
  initialPanel?: InitialPanel;
  /** Server-rendered interaction results for canonical landing pages */
  initialInteractionResults?: InteractionCheckResult[];
  /** Access mode for server-rendered interaction results */
  initialInteractionAccess?: 'none' | 'full' | 'paywalled';
  /** Paywall variant for server-rendered interaction results */
  initialInteractionPaywallVariant?: PaywallVariant | null;
  /** Whether pricing may be shown (price + /pricing CTA in the expired paywall) */
  showPricing: boolean;
  /** Called when the document panel opens or closes */
  onPanelChange?: (
    panel: { type: 'chpl' | 'ulotka'; drugId: string } | null,
  ) => void;
  /** When true, show drugs without ChPL extraction (admin only) */
  showUnavailableDrugs?: boolean;
  /** Server-rendered top combinations for SEO */
  initialTopCombinations?: import('./top-checked-combinations').CombinationDrug[];
  /** Override the default search footer. Can be a render function receiving `{ handleLoadExample }`. */
  searchFooterOverride?:
    | ReactNode
    | ((ctx: { handleLoadExample: () => Promise<void> }) => ReactNode);
  /** Additional class names for the search input element. Can be a function receiving `{ hasDrugs }`. */
  searchInputClassName?: string | ((ctx: { hasDrugs: boolean }) => string);
  /** Override placeholder text for the search input */
  searchPlaceholder?: string;
  /** Override top padding for the empty state (default: pt-[20vh] with beforeFooter, pt-[18vh] without) */
  emptyStatePaddingTop?: string;
  /** Maximum drugs that can be selected (tier-dependent) */
  maxDrugs?: number;
  /** Account is on a dedicated (custom) plan — the drug-limit upsell points to contact */
  hasCustomPlan?: boolean;
  /** Content rendered inside the search input on the right (desktop only) */
  searchInputEndAddon?:
    | ReactNode
    | ((ctx: { handleLoadExample: () => Promise<void> }) => ReactNode);
  /**
   * Which drug sets may be checked. `'example-only'` (the English demo) blocks
   * every set that is not a subset of the example drugs and shows an
   * explanatory card instead of running a check. Default: `'default'`.
   */
  interactionCheckPolicy?: InteractionCheckPolicy;
}

export type InteractionCheckPolicy = 'default' | 'example-only';

export { EXAMPLE_DRUG_IDS };

export function DrugInteractionsPageShell({
  isAuthenticated,
  isAuthLoading = false,
  hasActiveTrial = false,
  needsTrialActivation = false,
  onActivateTrial,
  initialDrugs,
  header,
  topBar,
  topBarHeight = 0,
  branding,
  footer,
  beforeFooter,
  onResetRef,
  onSetDrugsRef,
  enableUrlSync = false,
  initialPanel,
  initialInteractionResults,
  initialInteractionAccess = 'none',
  initialInteractionPaywallVariant = null,
  showPricing,
  onPanelChange,
  showUnavailableDrugs = false,
  initialTopCombinations,
  searchFooterOverride,
  searchInputClassName,
  searchPlaceholder,
  emptyStatePaddingTop,
  maxDrugs,
  hasCustomPlan = false,
  searchInputEndAddon,
  interactionCheckPolicy = 'default',
}: DrugInteractionsPageShellProps) {
  const { t } = useTranslation('common');
  const isHeaderVisible = useScrollDirection();
  const { panelWidth, handleMouseDown } = useResizablePanel(55, 25, 70);

  const [drugsCount, setDrugsCount] = useState(0);
  const hasDrugs = drugsCount > 0;
  const resolvedBranding =
    typeof branding === 'function' ? branding({ hasDrugs }) : branding;
  const [chplViewer, setChplViewer] = useState<ChplViewerState | null>(() => {
    if (!initialPanel) return null;

    const { type, drug } = initialPanel;
    const quote: ChplQuote = {
      drugId: drug.id,
      drugName: drug.productName,
      activeSubstance: drug.commonName || undefined,
      strength: drug.strength || undefined,
      form: drug.form || undefined,
      texts: [],
      url: `/api/chpl/${drug.id}`,
    };

    return {
      quote,
      viewKey: Date.now(),
      initialViewMode: 'pdf' as ViewMode,
      scrollToChapter45: false,
      documentType:
        type === 'ulotka'
          ? ('ulotka' as DocumentType)
          : ('chpl' as DocumentType),
      hasChplPdf: drug.hasChplPdf ?? false,
      hasUlotka: drug.hasUlotkaPdf ?? false,
      hasChplExtraction: drug.hasChplExtraction,
    };
  });

  const resetRef = useRef<(() => void) | null>(null);
  const setDrugsRef = useRef<((drugs: Drug[]) => void) | null>(null);
  const drugSearchScrollRef = useRef<HTMLDivElement | null>(null);
  const selectedDrugsRef = useRef<Drug[]>([]);
  const [_topCombinationsHasData, setTopCombinationsHasData] = useState(
    () => !!initialTopCombinations && initialTopCombinations.length > 0,
  );
  const resolvedBeforeFooter =
    typeof beforeFooter === 'function'
      ? beforeFooter({ hasDrugs })
      : beforeFooter;
  const hasUnauthBottomReveal = !isAuthenticated && !!beforeFooter;

  const mobileViewerRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // The header is `fixed top-0`. While the top bar is on screen the header sits
  // below it; once the bar scrolls away the header returns to the top. Written
  // as a CSS variable on scroll so it never re-renders.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const update = () => {
      const offset = Math.max(0, topBarHeight - window.scrollY);
      el.style.setProperty('--top-bar-offset', `${offset}px`);
    };

    update();
    // No bar (or dismissed): the offset stays 0, no need to track scrolling
    if (topBarHeight <= 0) return;
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, [topBarHeight]);

  const handleLoadExample = useCallback(async () => {
    try {
      const res = await fetch('/data/chpl_metadata/drugs.json', {
        cache: 'no-store',
      });
      const all = (await res.json()) as Drug[];
      const picked = all.filter((d) => EXAMPLE_DRUG_IDS.includes(d.id));
      if (picked.length > 0) {
        setDrugsRef.current?.(picked);
        // Scroll to drug search area so user sees the drug list first
        setTimeout(() => {
          drugSearchScrollRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 100);
      }
    } catch {
      // ignore
    }
  }, []);

  // handleLoadExample only reads setDrugsRef.current on user click, not during render.
  const resolvedSearchFooterOverride = useMemo(() => {
    if (searchFooterOverride === undefined) return undefined;
    if (typeof searchFooterOverride === 'function') {
      // eslint-disable-next-line react-hooks/refs -- ref is read lazily on click, not during render
      return searchFooterOverride({ handleLoadExample });
    }
    return searchFooterOverride;
  }, [searchFooterOverride, handleLoadExample]);

  const resolvedHeader = useMemo(() => {
    if (typeof header === 'function') {
      // eslint-disable-next-line react-hooks/refs -- ref is read lazily on click, not during render
      return header({ handleLoadExample });
    }
    return header;
  }, [header, handleLoadExample]);

  const resolvedSearchInputEndAddon = useMemo(() => {
    if (searchInputEndAddon === undefined) return undefined;
    if (typeof searchInputEndAddon === 'function') {
      // eslint-disable-next-line react-hooks/refs -- ref is read lazily on click, not during render
      return searchInputEndAddon({ handleLoadExample });
    }
    return searchInputEndAddon;
  }, [searchInputEndAddon, handleLoadExample]);

  const handleDrugsCountChange = useCallback((count: number) => {
    setDrugsCount(count);
  }, []);

  const handleSelectedDrugsChange = useCallback((drugs: Drug[]) => {
    selectedDrugsRef.current = drugs;
  }, []);

  const handleReset = useCallback(() => {
    resetRef.current?.();
    setChplViewer(null);
  }, []);

  const handleViewChpl = useCallback(
    (quote: ChplQuote, options?: ViewChplOptions) => {
      const openedFromQuote = options?.scrollToChapter45 === true;
      const initialMode: ViewMode = options?.showPdf
        ? 'pdf'
        : openedFromQuote
          ? 'markdown'
          : 'pdf';
      const scrollToChapter45 = options?.scrollToChapter45 ?? false;
      // Look up document availability from options or from selected drugs
      const drug = selectedDrugsRef.current.find((d) => d.id === quote.drugId);
      const hasUlotka = options?.hasUlotka ?? drug?.hasUlotkaPdf ?? false;
      const hasChplPdf = options?.hasChplPdf ?? drug?.hasChplPdf ?? false;
      // Ensure download URL is always set when drugId is available
      const quoteWithUrl = quote.url
        ? quote
        : {
            ...quote,
            url: quote.drugId ? `/api/chpl/${quote.drugId}` : undefined,
          };
      setChplViewer({
        quote: quoteWithUrl,
        viewKey: Date.now(),
        initialViewMode: initialMode,
        scrollToChapter45,
        documentType: 'chpl',
        hasChplPdf,
        hasUlotka,
        hasChplExtraction: drug?.hasChplExtraction,
      });
      onPanelChange?.({ type: 'chpl', drugId: quote.drugId ?? '' });
    },
    [onPanelChange],
  );

  const handleViewUlotka = useCallback(
    (quote: ChplQuote) => {
      const drug = selectedDrugsRef.current.find((d) => d.id === quote.drugId);
      setChplViewer({
        quote,
        viewKey: Date.now(),
        initialViewMode: 'pdf',
        scrollToChapter45: false,
        documentType: 'ulotka',
        hasChplPdf: drug?.hasChplPdf ?? false,
        hasUlotka: true,
        hasChplExtraction: drug?.hasChplExtraction,
      });
      onPanelChange?.({ type: 'ulotka', drugId: quote.drugId ?? '' });
    },
    [onPanelChange],
  );

  const handleCloseChpl = useCallback(() => {
    setChplViewer(null);
    onPanelChange?.(null);
  }, [onPanelChange]);

  const handleViewModeChange = useCallback((_mode: ViewMode) => {
    // No-op: view mode changes are local only
  }, []);

  // Expose reset to parent
  useEffect(() => {
    onResetRef?.(handleReset);
  }, [onResetRef, handleReset]);

  // Expose setDrugs to parent
  useEffect(() => {
    if (setDrugsRef.current) {
      onSetDrugsRef?.(setDrugsRef.current);
    }
  }, [onSetDrugsRef]);

  // Lock body scroll when mobile viewer overlay is open
  useEffect(() => {
    if (!chplViewer) return;

    const isMobile = window.innerWidth < 1024; // lg breakpoint
    if (!isMobile) return;

    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [chplViewer]);

  return (
    <div
      ref={rootRef}
      className="relative flex flex-1 flex-col"
      style={{ '--top-bar-offset': `${topBarHeight}px` } as React.CSSProperties}
    >
      {/* Top bar (announcement) - above the header, scrolls away with the page.
          z-[60] so the header slides under it when auto-hiding. Hidden behind
          the full-screen mobile viewer; narrowed next to the desktop panel. */}
      {topBar && (
        <div
          className={cn(
            'absolute inset-x-0 top-0 z-[60]',
            chplViewer && 'max-lg:hidden lg:[right:var(--panel-width)]',
          )}
          style={
            chplViewer
              ? ({ '--panel-width': `${panelWidth}%` } as React.CSSProperties)
              : undefined
          }
        >
          {topBar}
        </div>
      )}

      {/* Desktop ChPL Viewer - fixed right panel with resizable width */}
      {chplViewer && (
        <>
          {/* Resize handle - invisible hit area with centered grip */}
          <div
            onMouseDown={handleMouseDown}
            className="group fixed top-0 bottom-0 z-[60] hidden w-5 cursor-col-resize lg:block"
            style={{ left: `calc(${100 - panelWidth}% - 10px)` }}
          >
            {/* Vertical bar */}
            <div className="absolute inset-y-0 left-1/2 w-1.5 -translate-x-1/2 bg-zinc-200 transition-colors group-hover:bg-zinc-300 group-active:bg-zinc-400 dark:bg-zinc-700 group-hover:dark:bg-zinc-600 group-active:dark:bg-zinc-500" />
            {/* Three dots grip indicator - centered, fixed size */}
            <div className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col gap-[3px]">
              <div className="h-[3px] w-[3px] rounded-full bg-zinc-400 dark:bg-zinc-500" />
              <div className="h-[3px] w-[3px] rounded-full bg-zinc-400 dark:bg-zinc-500" />
              <div className="h-[3px] w-[3px] rounded-full bg-zinc-400 dark:bg-zinc-500" />
            </div>
          </div>

          {/* Right panel */}
          <div
            className="bg-background fixed top-0 right-0 bottom-0 z-40 hidden border-l border-zinc-100 px-4 pt-4 pb-3 lg:block dark:border-zinc-800 dark:bg-zinc-950"
            style={{ left: `${100 - panelWidth}%` }}
          >
            <div className="h-full">
              <ChplViewer
                key={chplViewer.viewKey}
                quote={chplViewer.quote}
                onClose={handleCloseChpl}
                initialViewMode={chplViewer.initialViewMode}
                scrollToChapter45={chplViewer.scrollToChapter45}
                onViewModeChange={handleViewModeChange}
                documentType={chplViewer.documentType}
                hasChplPdf={chplViewer.hasChplPdf}
                hasUlotka={chplViewer.hasUlotka}
                hasChplExtraction={chplViewer.hasChplExtraction}
              />
            </div>
          </div>
        </>
      )}

      {/* Header - auto-hides on scroll */}
      {resolvedHeader && (
        <header
          className={cn(
            'bg-background/80 fixed top-0 right-0 left-0 z-50 flex h-14 items-center border-b border-zinc-100 px-4 backdrop-blur-md transition-transform duration-300 ease-in-out dark:border-zinc-800',
            isHeaderVisible ? 'translate-y-0' : '-translate-y-full',
            // Use CSS variable + lg: prefix to ensure right offset only applies when side panel is visible
            chplViewer && 'lg:[right:var(--panel-width)]',
          )}
          style={
            {
              top: 'var(--top-bar-offset, 0px)',
              ...(chplViewer ? { '--panel-width': `${panelWidth}%` } : {}),
            } as React.CSSProperties
          }
        >
          {resolvedHeader}
        </header>
      )}

      {/* Spacer for the top bar (0 when absent or dismissed) */}
      {topBarHeight > 0 && <div style={{ height: topBarHeight }} />}

      {/* Spacer for fixed header height */}
      {resolvedHeader && <div className="h-14" />}

      {/* Main content area */}
      <div
        className={cn(
          'flex flex-1 flex-col items-center px-4 transition-[padding] duration-500 ease-out',
          hasUnauthBottomReveal && 'min-h-[calc(100svh-3.5rem)]',
          hasUnauthBottomReveal &&
            (hasDrugs ? 'pt-12' : (emptyStatePaddingTop ?? 'pt-[20vh]')),
          !hasUnauthBottomReveal &&
            (hasDrugs ? 'pt-12' : (emptyStatePaddingTop ?? 'pt-[18vh]')),
          // Use CSS variable + lg: prefix to ensure margin only applies when side panel is visible
          chplViewer && 'lg:[margin-right:var(--panel-width)] lg:px-6',
        )}
        style={
          chplViewer
            ? ({ '--panel-width': `${panelWidth}%` } as React.CSSProperties)
            : undefined
        }
      >
        <div
          className={cn(
            '@container flex w-full max-w-3xl flex-1 flex-col',
            'pb-20 @[640px]:pb-28',
          )}
        >
          {/* Branding (logo/title) above search */}
          {resolvedBranding}

          <DrugInteractionsContainer
            isAuthenticated={isAuthenticated}
            isAuthLoading={isAuthLoading}
            hasActiveTrial={hasActiveTrial}
            needsTrialActivation={needsTrialActivation}
            onActivateTrial={onActivateTrial}
            initialDrugs={initialDrugs}
            initialInteractionResults={initialInteractionResults}
            initialInteractionAccess={initialInteractionAccess}
            initialInteractionPaywallVariant={initialInteractionPaywallVariant}
            showPricing={showPricing}
            enableUrlSync={enableUrlSync}
            onDrugsCountChange={handleDrugsCountChange}
            onSelectedDrugsChange={handleSelectedDrugsChange}
            onResetRef={(reset) => {
              resetRef.current = reset;
            }}
            onSetDrugsRef={(setDrugs) => {
              setDrugsRef.current = setDrugs;
            }}
            autoCheckOnSet
            onDrugSearchRef={(ref) => {
              drugSearchScrollRef.current = ref.current;
            }}
            onViewChpl={handleViewChpl}
            onViewUlotka={handleViewUlotka}
            showUnavailableDrugs={showUnavailableDrugs}
            maxDrugs={maxDrugs}
            hasCustomPlan={hasCustomPlan}
            initialTopCombinations={initialTopCombinations}
            onTopCombinationsHasData={setTopCombinationsHasData}
            checkPolicy={interactionCheckPolicy}
            onLoadExample={() => void handleLoadExample()}
            placeholder={searchPlaceholder}
            searchInputClassName={
              typeof searchInputClassName === 'function'
                ? searchInputClassName({ hasDrugs })
                : searchInputClassName
            }
            searchInputEndAddon={resolvedSearchInputEndAddon}
            searchFooter={
              resolvedSearchFooterOverride !== undefined ? (
                resolvedSearchFooterOverride
              ) : !isAuthenticated ? (
                <div className="mt-5 flex flex-col items-center gap-1 @[640px]:flex-row @[640px]:justify-center @[640px]:gap-0">
                  <span className="text-sm text-zinc-400 @[640px]:hidden">
                    {t('drugInteractionsPage.or')}
                  </span>
                  <button
                    type="button"
                    onClick={() => void handleLoadExample()}
                    className="group inline-flex cursor-pointer items-center gap-1.5 text-sm text-zinc-500 transition-all duration-200 hover:tracking-wide hover:text-zinc-800 focus:outline-none @[640px]:gap-2 @[640px]:text-base dark:text-zinc-400 dark:hover:text-zinc-200"
                  >
                    <span className="hidden @[640px]:inline">
                      {t('drugInteractionsPage.or')}{' '}
                    </span>
                    <span className="relative font-medium text-zinc-700 dark:text-zinc-300">
                      <span className="relative z-10">
                        {t('drugInteractionsPage.seeExampleAnalysisShort')}
                      </span>
                      <span className="absolute right-0 bottom-0 left-0 z-0 h-[0.3em] -skew-x-3 rounded-[2px] bg-green-300/70 transition-all duration-200 group-hover:h-[0.8em] group-hover:bg-green-300 dark:bg-green-500/25 dark:group-hover:bg-green-500/40" />
                    </span>
                    <span className="text-zinc-400 dark:text-zinc-500">
                      ({t('drugInteractionsPage.seeExampleCount')})
                    </span>
                    <ArrowRight className="h-3.5 w-3.5 text-zinc-400 transition-transform duration-200 group-hover:translate-x-1 dark:text-zinc-500" />
                  </button>
                </div>
              ) : undefined
            }
          />
        </div>
      </div>

      {/* Mobile ChPL Viewer - fixed full-screen overlay on small screens */}
      {chplViewer && (
        <div
          ref={mobileViewerRef}
          className="fixed inset-0 z-50 flex flex-col bg-white px-3 pt-[max(0.75rem,env(safe-area-inset-top))] pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:hidden dark:bg-zinc-950"
        >
          <div className="h-full">
            <ChplViewer
              key={chplViewer.viewKey}
              quote={chplViewer.quote}
              onClose={handleCloseChpl}
              initialViewMode={chplViewer.initialViewMode}
              scrollToChapter45={chplViewer.scrollToChapter45}
              onViewModeChange={handleViewModeChange}
              documentType={chplViewer.documentType}
              hasChplPdf={chplViewer.hasChplPdf}
              hasUlotka={chplViewer.hasUlotka}
              hasChplExtraction={chplViewer.hasChplExtraction}
            />
          </div>
        </div>
      )}

      {resolvedBeforeFooter && (
        <div
          className={cn(
            '@container',
            chplViewer && 'lg:[margin-right:var(--panel-width)]',
          )}
          style={
            chplViewer
              ? ({ '--panel-width': `${panelWidth}%` } as React.CSSProperties)
              : undefined
          }
        >
          {resolvedBeforeFooter}
        </div>
      )}
      {footer({ isViewerOpen: !!chplViewer, panelWidth })}
    </div>
  );
}
